import React, { useState, useRef, ReactNode } from 'react';
import { Upload } from 'antd';
import cx from 'classnames';
import {
  RcFile,
  UploadFile,
  UploadProps,
  UploadChangeParam,
  ShowUploadListInterface,
} from 'antd/lib/upload/interface';
import { UploadRequestOption } from 'rc-upload/lib/interface';
import useDeepCompareEffect from 'use-deep-compare-effect';
import axios, { Canceler } from 'axios';
import { debounce, isNil, omit } from 'lodash';

import BaseAlert from 'components/wrappers/BaseAlert';
import { S3UploadResponse } from 'api/backofficeApiClient';
import { BaseUploadMessages, getFileExt } from 'utils/fileUtils';

import './BaseFileUpload.less';

type OwnProps = {
  value?: string[] | FileValue[];
  disableOnLoading?: boolean;
};

export interface FileValue {
  fileName: string;
  s3Key: string;
}

export enum FileUploadTypeEnum {
  Instant = 1,
  Manual = 2,
}

export type BaseFileUploadProps = {
  children?: (props: UploadItemProps) => ReactNode;
  isDraggable?: boolean;
  error?: string;
  handleFieldTouch?: () => void;
  maxFileCount?: number;
  fileNameLength?: number;
  acceptedFileSize: number;
  includeFileName?: boolean;
  isUpdatedDefaultFileList?: boolean;
  acceptedFileFormat: string[];
  defaultFileList?: BaseUploadFile[];
  uploadLinkFunction?: Function;
  messages: BaseUploadMessages;
  typeUploader?: FileUploadTypeEnum;
  getUploadList?: (
    props: UploadItemProps,
  ) => boolean | ShowUploadListInterface | undefined;
} & UploadProps;

type FileUploadProps = BaseFileUploadProps & OwnProps;

export type UploadItemProps = {
  isUploading: boolean;
  isLoading: boolean;
  loadingFileKeys: IFilesStatus;
  uploadingFileKeys: IFilesStatus;
};

interface ICancelRequests {
  [key: string]: Canceler;
}

interface IFilesStatus {
  [key: string]: boolean;
}

export type BaseUploadFile = UploadFile<S3UploadResponse>;

const { Dragger } = Upload;
const CancelToken = axios.CancelToken;

export default function BaseFileUpload(props: FileUploadProps) {
  const {
    name,
    onChange,
    children,
    maxFileCount = 1,
    fileNameLength,
    acceptedFileSize,
    acceptedFileFormat,
    isDraggable = false,
    value,
    messages,
    uploadLinkFunction,
    getUploadList,
    defaultFileList = [],
    handleFieldTouch,
    error,
    disableOnLoading = false, // can be overrided by direct disabled property
    fileList: files = defaultFileList,
    typeUploader,
    ...rest
  } = props;
  const [fileList, setFileList] = useState<BaseUploadFile[]>(defaultFileList);
  const [loadingFileKeys, setLoadingFileKeys] = useState<IFilesStatus>({}); // getting link + uploading
  const [uploadingFileKeys, setUploadingFileKeys] = useState<IFilesStatus>({}); // only uploading
  const isLoading = Object.values(loadingFileKeys).some(Boolean);
  const isUploading = Object.values(uploadingFileKeys).some(Boolean);
  const acceptedUploadFormat = acceptedFileFormat.join(',');
  const cancelRef = useRef<ICancelRequests>({});
  const isMultipleUpload = maxFileCount > 1;
  const showError = !!error && !isLoading;

  useDeepCompareEffect(() => {
    setFileList(files);
  }, [files]);

  const handleLoadingFile = (file: RcFile, isLoading: boolean) =>
    setLoadingFileKeys((loadingFileKeys: any) => ({
      ...loadingFileKeys,
      [file.uid]: isLoading,
    }));

  const handleUploadingFile = (file: RcFile, isUploading: boolean) =>
    setUploadingFileKeys((uploadingFileKeys: any) => ({
      ...uploadingFileKeys,
      [file.uid]: isUploading,
    }));

  const removeLoadingFileKey = (fileKey: string) =>
    setLoadingFileKeys((loadingFileKeys) => omit(loadingFileKeys, [fileKey]));

  const removeUploadingFileKey = (fileKey: string) =>
    setUploadingFileKeys((uploadingFileKeys) =>
      omit(uploadingFileKeys, [fileKey]),
    );

  const clearFileKey = (fileKey: string) => {
    removeLoadingFileKey(fileKey);
    removeUploadingFileKey(fileKey);
  };

  const getFileType = (file: RcFile | File) =>
    file.type || `.${getFileExt(file.name)}`;

  const isFileFormatValid = (file: RcFile | File) => {
    const type = getFileType(file);

    return acceptedFileFormat.some(
      (format: string) => format.toLowerCase() === type.toLowerCase(),
    );
  };

  const isFileSizeValid = (file: File | RcFile) =>
    file.size <= 1024 * 1000 * acceptedFileSize;

  const isFileCounterValid = (newFileList: RcFile[] | UploadFile[]) => {
    const fileListCounter = fileList.concat(newFileList).length;

    return maxFileCount >= fileListCounter;
  };

  const isFileNameLengthValid = (file: RcFile | File | UploadFile) =>
    file?.name?.length < (fileNameLength || 0);

  const debounceErrorMessage = debounce(
    (message) => BaseAlert.error(message),
    100,
  );

  const hasValidationError = (file: File | RcFile, fileList: RcFile[]) => {
    const errorMessages: string[] = [];

    if (isMultipleUpload && !isFileCounterValid(fileList)) {
      debounceErrorMessage(messages.maxFileCounterError(maxFileCount));
      return true;
    }

    if (!isFileSizeValid(file)) {
      errorMessages.push(
        messages.fileSizeLimitError(file.name, acceptedFileSize),
      );
    }
    if (!isNil(fileNameLength) && !isFileNameLengthValid(file)) {
      errorMessages.push(
        messages.fileNameLengthError(file.name, fileNameLength),
      );
    }
    if (!isFileFormatValid(file)) {
      errorMessages.push(messages.fileUnsupportedError(file.name));
    }

    const hasError = errorMessages.length > 0;

    if (hasError) {
      BaseAlert.error(errorMessages.join(' '));
    }
    return hasError;
  };

  const handleBeforeUpload = (
    file: RcFile,
    fileList: RcFile[],
  ): Promise<void> | string | boolean => {
    const hasError = hasValidationError(file, fileList);

    if (hasError) {
      return Upload.LIST_IGNORE;
    }

    if (typeUploader === FileUploadTypeEnum.Manual) return false;

    if (!isMultipleUpload) {
      setFileList([]);
    }

    return Promise.resolve();
  };

  const handleChange = (info: UploadChangeParam) => {
    const { fileList } = info;

    const newFileList = fileList.filter(
      (file: BaseUploadFile) => file.status !== 'error',
    );
    const fileInfo = { ...info, fileList: newFileList };

    setFileList(newFileList);
    onChange && onChange(fileInfo);
  };

  const handleRequest = async (options: UploadRequestOption): Promise<void> => {
    if (!uploadLinkFunction) return;

    const { onSuccess, onError, onProgress } = options;
    const file = options.file as RcFile;
    const fileExt = getFileExt(file.name);

    handleLoadingFile(file, true);

    try {
      const { s3Key, contentType, putUrl, getUrl } =
        await uploadLinkFunction(fileExt);
      handleUploadingFile(file, true);

      const result: any = await axios.put(putUrl, file, {
        onUploadProgress: (e) => {
          onProgress?.({ ...e, percent: (e.loaded / e.total) * 100 });
        },
        headers: {
          'Content-Type': contentType,
        },
        cancelToken: new CancelToken(function executor(c) {
          cancelRef.current[file.uid] = c;
        }),
      });

      onSuccess?.({ ...result.body, s3Key, getUrl }, result);
    } catch (err) {
      if (axios.isCancel(err)) return;

      BaseAlert.error(messages.uploadFailed(file.name));
      onError?.(err as any);
    } finally {
      handleLoadingFile(file, false);
      handleUploadingFile(file, false);
    }
  };

  const cancelRequest = (fileUid: string) => {
    if (!cancelRef.current[fileUid]) return;

    cancelRef.current[fileUid]();

    delete cancelRef.current[fileUid];
  };

  const handleRemove = (file: BaseUploadFile) => {
    const { uid } = file;
    // Do not remove file while getting links
    if (loadingFileKeys[uid] && !uploadingFileKeys[uid]) return false;

    clearFileKey(uid);
    cancelRequest(uid);

    return true;
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    const files = Object.values(event.dataTransfer.files);

    files.forEach((file) => {
      if (!isFileFormatValid(file)) {
        BaseAlert.error(messages.fileUnsupportedError(file.name));
      }
    });
  };

  const handleBlur = () => handleFieldTouch?.();

  const renderUploadItem = () => {
    const props: UploadItemProps = {
      isUploading,
      isLoading,
      loadingFileKeys,
      uploadingFileKeys,
    };
    const UploadItemComponent = isDraggable ? Dragger : Upload;

    return (
      <div onBlur={handleBlur}>
        <UploadItemComponent
          name={name}
          multiple={isMultipleUpload}
          className={cx({
            'is-error': showError,
          })}
          onDrop={handleDrop}
          fileList={fileList}
          disabled={disableOnLoading && isLoading}
          onRemove={handleRemove}
          onChange={handleChange}
          customRequest={handleRequest}
          accept={acceptedUploadFormat}
          showUploadList={getUploadList ? getUploadList(props) : false}
          beforeUpload={handleBeforeUpload}
          {...rest}
        >
          {children && children(props)}
        </UploadItemComponent>

        {showError && (
          <div className="ant-form-item-explain ant-form-item-explain-error">
            {error}
          </div>
        )}
      </div>
    );
  };

  return <>{renderUploadItem()}</>;
}
